/**
 * アリーナの問い合わせのダイアログ（React の DOM）。area（prompt.dialogArea）を渡すと盤の外の置き場に置く（盤にかけない）。
 * - 行動（メイン / バトルフェイズ）: 印を押して開く。フェイズの移行とカードごとの行動（召喚・発動・攻撃…）
 * - それ以外（チェーンの確認・カードの選択・効果の選択・表示形式・置く場所…）: 問い合わせが来たら自動で出す
 * 押した payload は onRespond に、選択・並べ替え・数え・ビット・宣言は onAnswer({ picks | value | mask }) に渡すだけ（答えに直すのはページとサーバー。Issue #665）。
 */
import { useEffect, useState } from 'react';
import { playerColor, usePlayerColors } from './common';

const S = {
  overlay: { position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(5,6,12,0.55)', zIndex: 5, padding: 12 },
  panel: { background: '#0f1526', color: '#e8eefc', borderRadius: 12, border: '1px solid #2a3552', width: 'min(560px, 100%)', maxHeight: '80%', display: 'flex', flexDirection: 'column', boxShadow: '0 12px 40px rgba(0,0,0,0.6)' },
  head: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderBottom: '1px solid #2a3552' },
  body: { padding: 12, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 },
  foot: { display: 'flex', gap: 8, padding: '10px 14px', borderTop: '1px solid #2a3552', justifyContent: 'flex-end', flexWrap: 'wrap' },
  note: { fontSize: 12, opacity: 0.8 },
  group: { fontSize: 12, opacity: 0.75, marginTop: 6 },
};
const btn = (variant = 'plain', on = false) => ({
  padding: '10px 14px', borderRadius: 10, fontSize: 15, cursor: 'pointer', textAlign: 'left', minHeight: 44,
  border: `1px solid ${variant === 'primary' || on ? '#4fc3ff' : '#345'}`,
  background: variant === 'primary' ? '#1b4a6d' : on ? '#1b3a55' : '#151c30', color: '#e8eefc',
});

function Frame({ title, player, players, hint, note, children, footer, onClose, testId, area = null }) {
  const colors = usePlayerColors();
  const color = playerColor(player, colors) ?? colors[0];
  const name = players?.[player]?.name ?? `P${player + 1}`;
  // area.inline: 見出しを持たず中身と決定の列だけを流れに置く（入力画面の応答タブが自分の見出しの下に埋め込む。Issue #467）
  if (area?.inline) {
    return (
      <div data-testid={testId} role="group" aria-label={title} style={{ display: 'flex', flexDirection: 'column', gap: 8, color: '#e8eefc' }}>
        {note ? <div style={S.note}>{note}</div> : null}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{children}</div>
        {footer ? <div style={{ ...S.foot, padding: '6px 0 0', borderTop: 'none' }}>{footer}</div> : null}
      </div>
    );
  }
  // area があれば盤の外の置き場（stage の中の絶対配置。盤にかけない）。無ければ中央に重ねる
  const overlay = area ? { position: 'absolute', ...area.style, display: 'flex', alignItems: 'flex-start', justifyContent: 'stretch', zIndex: 5, pointerEvents: 'none' } : S.overlay;
  const panel = area ? { ...S.panel, pointerEvents: 'auto', width: '100%', maxHeight: '100%' } : S.panel;
  return (
    <div style={overlay} data-testid={testId} data-side={area?.side ?? undefined} onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div style={panel} role="dialog" aria-label={title}>
        <div style={S.head}>
          <span style={{ width: 12, height: 12, borderRadius: 999, background: color, flexShrink: 0 }} />
          <span style={{ fontSize: 12, opacity: 0.8 }}>{name}（P{player + 1}）</span>
          <strong style={{ fontSize: 15, marginLeft: 'auto' }}>{title}</strong>
          {onClose ? <button type="button" aria-label="閉じる" onClick={onClose} style={{ ...btn(), minHeight: 32, padding: '4px 10px' }}>✕</button> : null}
        </div>
        <div style={S.body}>
          {hint ? <div style={S.note}>{hint}</div> : null}
          {note ? <div style={S.note}>{note}</div> : null}
          {children}
        </div>
        {footer ? <div style={S.foot}>{footer}</div> : null}
      </div>
    </div>
  );
}

/** 問い合わせ（自動で出す）。spec は pendingChoices.choicesFor の戻り値 */
export function PromptDialog({ dialog, players, busy, onRespond, onAnswer = () => {}, area = null }) {
  const [picked, setPicked] = useState([]);
  const [order, setOrder] = useState([]);
  const [counts, setCounts] = useState([]);
  const [mask, setMask] = useState(0);
  const [code, setCode] = useState('');
  const key = dialog ? `${dialog.title}:${JSON.stringify(dialog.spec?.choices?.map((c) => c.key) ?? dialog.spec?.kind)}` : '';
  useEffect(() => { setPicked([]); setOrder([]); setCounts([]); setMask(0); setCode(''); }, [key]);
  if (!dialog) return null;
  const { spec } = dialog;
  const frame = (props) => <Frame title={dialog.title} player={dialog.player} players={players} hint={dialog.hint} note={spec?.note} testId="arena-prompt" area={area} {...props} />;
  if (!spec || spec.kind === 'unsupported') return frame({ children: <div style={S.note}>この問い合わせにはまだ対応していません</div> });
  if (spec.kind === 'single') {
    return frame({ children: spec.choices.map((c) => <button key={c.key} type="button" disabled={busy} style={btn(c.payload?.pass || c.payload?.yes === false ? 'plain' : 'primary')} onClick={() => onRespond(c.payload)}>{c.label}</button>) });
  }
  if (spec.kind === 'multi') {
    const weight = picked.reduce((s, i) => s + (spec.choices.find((c) => c.index === i)?.weight ?? 1), 0);
    const ready = picked.length >= (spec.minCount ?? spec.min ?? 0) && picked.length <= (spec.max ?? Infinity) && weight >= (spec.min ?? 0);
    const toggle = (i) => setPicked((p) => (p.includes(i) ? p.filter((x) => x !== i) : (spec.max === 1 ? [i] : [...p, i])));
    return frame({
      children: spec.choices.map((c) => <button key={c.key} type="button" disabled={busy} style={btn('plain', picked.includes(c.index))} onClick={() => toggle(c.index)}>{picked.includes(c.index) ? '✓ ' : ''}{c.label}</button>),
      footer: <>
        {spec.cancel ? <button type="button" disabled={busy} style={btn()} onClick={() => onRespond(spec.cancel)}>やめる</button> : null}
        {spec.finish ? <button type="button" disabled={busy} style={btn()} onClick={() => onRespond(spec.finish)}>終える</button> : null}
        <button type="button" disabled={busy || !ready} style={btn('primary')} onClick={() => onAnswer({ picks: picked })}>決定（{picked.length}）</button>
      </>,
    });
  }
  if (spec.kind === 'order') {
    const rest = spec.cards.filter((c) => !order.includes(c.index));
    return frame({
      children: <>
        <div style={S.note}>選んだ順: {order.map((i) => spec.cards[i].label).join(' → ') || '—'}</div>
        {rest.map((c) => <button key={c.index} type="button" disabled={busy} style={btn()} onClick={() => setOrder((o) => [...o, c.index])}>{c.label}</button>)}
      </>,
      footer: <>
        <button type="button" disabled={busy} style={btn()} onClick={() => setOrder([])}>やり直す</button>
        {spec.skip ? <button type="button" disabled={busy} style={btn()} onClick={() => onRespond(spec.skip)}>並べ替えない</button> : null}
        <button type="button" disabled={busy || rest.length > 0} style={btn('primary')} onClick={() => onAnswer({ value: order })}>決定</button>
      </>,
    });
  }
  if (spec.kind === 'counts') {
    const vals = spec.cards.map((c, i) => counts[i] ?? 0);
    const total = vals.reduce((s, v) => s + v, 0);
    return frame({
      children: spec.cards.map((c, i) => (
        <label key={c.index} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
          <span style={{ flex: 1 }}>{c.label}</span>
          <input type="number" min={0} max={c.max} value={vals[i]} style={{ width: 64 }} onChange={(e) => setCounts((cs) => { const n = [...vals]; n[i] = Math.max(0, Math.min(c.max, Number(e.target.value) || 0)); return n; })} />
        </label>
      )),
      footer: <button type="button" disabled={busy || total !== spec.total} style={btn('primary')} onClick={() => onAnswer({ value: vals })}>決定（{total} / {spec.total}）</button>,
    });
  }
  if (spec.kind === 'bits') {
    const n = spec.options.filter((o) => mask & o.bit).length;
    return frame({
      children: spec.options.map((o) => <button key={o.bit} type="button" disabled={busy} style={btn('plain', !!(mask & o.bit))} onClick={() => setMask((m) => m ^ o.bit)}>{o.label}</button>),
      footer: <button type="button" disabled={busy || n !== spec.count} style={btn('primary')} onClick={() => onAnswer({ mask })}>決定</button>,
    });
  }
  if (spec.kind === 'code') {
    return frame({
      children: <>
        <select value={code} style={{ padding: 8, fontSize: 14 }} onChange={(e) => setCode(e.target.value)}>
          <option value="">カードを選ぶ</option>
          {spec.candidates.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
        </select>
        <input type="number" placeholder="code を直接入力" value={code} style={{ padding: 8 }} onChange={(e) => setCode(e.target.value)} />
      </>,
      footer: <button type="button" disabled={busy || !(Number(code) > 0)} style={btn('primary')} onClick={() => onAnswer({ value: Number(code) })}>決定</button>,
    });
  }
  return frame({ children: <div style={S.note}>この問い合わせにはまだ対応していません</div> });
}
